'use client';
import Button from '@/components/Button';
import FootballLines from '@/components/layout/FootballLines';
import SmartLink from '@/components/SmartLink';
import { cn } from '@/lib/utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { imgBadges2025BadgeSvg, imgLogosPrimaryLogoSvg, imgUiBracketLeftSvg, imgUiShopSvg } from '@public';
import { Urls } from '@urls';
import Image from 'next/image';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';

export type NavItem = {
	label: string;
	href: string;
};

/**
 * The event and Box Score links point at the featured event and game (the layout looks them up);
 * either is left out when nothing is featured, rather than linking somewhere fixed.
 */
const buildNavItems = (eventHref?: string | null, gameHref?: string | null): NavItem[] =>
	[
		{ label: 'about', href: Urls.About },
		eventHref ? { label: 'event', href: eventHref } : null,
		{ label: 'tickets', href: Urls.VivenuTickets },
		{ label: 'sfa', href: Urls.Sfa },
		{ label: 'cch', href: Urls.Cch },
		gameHref ? { label: 'Box Score', href: gameHref } : null,
	].filter((item): item is NavItem => item !== null);

const baseNavItemClass =
	'font-beachwood font-bold text-otnLime overflow-hidden truncate leading-[120%] uppercase text-center tracking-wide';
const navItemsContainerClassDesktop = 'hidden md:items-center md:w-[230px] md:justify-around md:flex';
const navItemClassDesktop = 'lg:text-[19px] md:text-[16px]';
const navItemClassMobile = 'text-[28px]';

const _Nav = ({
	navItems,
	isOpen,
	onClick,
	onLogoClick,
}: {
	navItems: NavItem[];
	isOpen: boolean;
	onClick: () => void;
	onLogoClick: () => void;
}) => {
	const midIndex = Math.ceil(navItems.length / 2);
	const leftNavItems = navItems.slice(0, midIndex);
	const rightNavItems = navItems.slice(midIndex);

	return (
		<nav
			className="page-shell flex w-full min-w-0 items-center justify-center gap-1 py-2 md:gap-4 md:py-4"
			aria-label="Main navigation"
		>
			<Image src={imgBadges2025BadgeSvg} alt="2025 event badge" className="h-[24px] w-[32px] lg:h-[40px] lg:w-[53px]" />

			<div className="h-[40px] max-h-[64px] min-w-0 shrink grow basis-[78px] lg:h-[64px]">
				<Image src={imgUiBracketLeftSvg} alt="" aria-hidden="true" className="h-full w-full" />
			</div>

			<div className={navItemsContainerClassDesktop}>
				{leftNavItems.map((item) => (
					<SmartLink key={item.href} href={item.href} className={cn(baseNavItemClass, navItemClassDesktop)}>
						{item.label}
					</SmartLink>
				))}
			</div>

			<div className="h-auto w-[86px] md:w-[130px]">
				<SmartLink key={'home'} href={Urls.Home} onClick={onLogoClick}>
					<Image src={imgLogosPrimaryLogoSvg} alt="Overtime Nationals Logo" className="w-full" />
				</SmartLink>
			</div>

			<div className={navItemsContainerClassDesktop}>
				{rightNavItems.map((item) => (
					<SmartLink key={item.href} href={item.href} className={cn(baseNavItemClass, navItemClassDesktop)}>
						{item.label}
					</SmartLink>
				))}
			</div>

			<div className="h-[40px] max-h-[64px] min-w-0 shrink grow basis-[78px] lg:h-[64px]">
				<Image src={imgUiBracketLeftSvg} alt="" aria-hidden="true" className="h-full w-full rotate-180" />
			</div>

			<Image src={imgBadges2025BadgeSvg} alt="2025 event badge" className="h-[24px] w-[32px] lg:h-[40px] lg:w-[53px]" />

			<button
				type="button"
				aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
				aria-expanded={isOpen}
				aria-controls="main-mobile-menu"
				onClick={onClick}
				className="bg-otnBlue absolute top-0 right-0 flex h-[32px] w-[44px] items-center justify-center md:hidden"
			>
				<FontAwesomeIcon
					icon={isOpen ? (faXmark as IconProp) : (faBars as IconProp)}
					width={24}
					height={24}
					className="text-[24px]"
				/>
			</button>
		</nav>
	);
};

const _MobileMenu = ({ navItems, isOpen, onClick }: { navItems: NavItem[]; isOpen: boolean; onClick: () => void }) => {
	const shopButtonContent = (
		<>
			<Image src={imgUiShopSvg} alt="Shop" width={24} />
			<span className="ml-2">Shop</span>
		</>
	);

	return (
		<div
			className={`absolute inset-x-0 top-full z-40 flex h-[calc(100vh-100%)] justify-center overflow-hidden transition-[max-height] duration-300 ease-out md:hidden ${
				isOpen ? 'pointer-events-auto max-h-[calc(100vh-100%)]' : 'pointer-events-none max-h-0'
			}`}
			onClick={onClick}
		>
			<div className="bg-otnBlack/[0.97] flex h-full w-full" onClick={(e) => e.stopPropagation()}>
				<nav className="flex h-full w-full flex-col overflow-y-auto px-[24px] pt-[48px] pb-[24px]">
					<div className="flex flex-col gap-4">
						{navItems.map((item: NavItem) => (
							<SmartLink
								key={item.href}
								href={item.href}
								className={cn(baseNavItemClass, navItemClassMobile)}
								onClick={onClick}
							>
								{item.label}
							</SmartLink>
						))}
					</div>

					<Button
						content={shopButtonContent}
						variant="secondary"
						href={Urls.OvertimeShop}
						className="mt-auto border text-[19px] font-bold uppercase"
						ariaLabel="Shop"
					/>
				</nav>
			</div>
		</div>
	);
};

const Header = ({ eventHref, gameHref }: { eventHref?: string | null; gameHref?: string | null }) => {
	const navItems = buildNavItems(eventHref, gameHref);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const toggleMobileMenu = (e?: MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		setIsMobileMenuOpen((prev) => !prev);
	};

	const closeMobileMenu = () => {
		if (isMobileMenuOpen) {
			setIsMobileMenuOpen(false);
		}
	};

	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.classList.add('overflow-hidden');
		} else {
			document.body.classList.remove('overflow-hidden');
		}

		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	}, [isMobileMenuOpen]);

	return (
		<header
			className="bg-otnBlack sticky top-0 z-50 shadow-[0_12px_28px_0_rgba(0,0,0,0.20),_0_2px_4px_0_rgba(0,0,0,0.10),_0_0_0_1px_rgba(255,255,255,0.05)_inset]"
			onClick={closeMobileMenu}
		>
			<FootballLines color="bg-[#616161]" />
			<div className="flex justify-center">
				<_Nav navItems={navItems} isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} onLogoClick={closeMobileMenu} />
			</div>
			<_MobileMenu navItems={navItems} isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />
		</header>
	);
};

export default Header;
